import type { ReactNode } from "react";
import { cn } from "@/shared/cn";
import { avatarToneProps } from "@/shared/ui/primitives/Avatar";
import { PanelToggle } from "@/shared/ui/primitives/PanelToggle";
import { FrameOpener } from "@/shared/ui/layout/FrameOpener";
import { PanelLeftCloseIcon, PanelLeftOpenIcon, SearchIcon } from "@/shared/ui/layout/shell-icons";

export interface SideMenuProps {
  collapsed: boolean;
  onToggle: () => void;
  labels: { open: string; collapse: string; navigation: string };
  search: ReactNode;
  footer: ReactNode;
  miniFooter: ReactNode;
  children: ReactNode;
}

export function SideMenu({
  collapsed,
  onToggle,
  labels,
  search,
  footer,
  miniFooter,
  children,
}: SideMenuProps) {
  return (
    <aside
      aria-label={labels.navigation}
      data-frame="side"
      data-collapsed={collapsed || undefined}
      data-tour={collapsed ? undefined : "menu"}
      className={cn(
        "relative flex min-h-0 shrink-0 flex-col border-r border-(--line) bg-(--z-side) pb-[10px]",
        "transition-[width] duration-[280ms] ease-(--ease-frame) max-phone:hidden",
        collapsed ? "w-(--rail-w)" : "w-(--side-w)",
      )}
    >
      {collapsed ? (
        <>
          <FrameOpener label={labels.open} onClick={onToggle} tour="menu">
            <PanelLeftOpenIcon />
          </FrameOpener>
          <div
            data-tour="rail"
            className="absolute inset-x-0 bottom-[14px] flex flex-col items-center gap-[6px]"
          >
            {miniFooter}
          </div>
        </>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="flex h-(--head-h) shrink-0 items-center justify-between pr-[16px] pl-[16px]">
            <Wordmark />
            <PanelToggle label={labels.collapse} onClick={onToggle}>
              <PanelLeftCloseIcon />
            </PanelToggle>
          </div>
          <div className="shrink-0">{search}</div>
          <div data-side-scroll className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            {children}
          </div>
          <div
            data-side-footer
            data-tour="rail"
            className="relative z-[2] flex shrink-0 flex-col gap-[10px] border-t border-(--line) bg-(--z-side) px-[14px] py-[12px]"
          >
            {footer}
          </div>
        </div>
      )}
    </aside>
  );
}

function Wordmark({ size = 19 }: { size?: number }) {
  return (
    <div
      data-type-scale="exempt"
      className="font-medium tracking-[-0.02em] text-(--ink)"
      style={{ fontSize: size }}
    >
      Life<i className="text-(--brand-orange) not-italic">:</i>Timing
    </div>
  );
}

export function SideSearch({
  value,
  onChange,
  onEnter,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  onEnter: () => void;
  label: string;
}) {
  return (
    <label className="mx-[12px] mt-[4px] mb-[10px] flex items-center gap-[8px] rounded-[12px] border border-(--line) bg-(--paper) px-[10px] py-[8px] text-sm text-(--muted) focus-within:border-(--brand)">
      <SearchIcon />
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
          event.preventDefault();
          onEnter();
        }}
        placeholder={label}
        aria-label={label}
        className="w-full border-0 bg-transparent text-(--ink) outline-0 placeholder:text-(--muted)"
      />
    </label>
  );
}

export function SideSection({
  title,
  count,
  children,
}: {
  title: string;
  count?: number | null;
  children: ReactNode;
}) {
  return (
    <section aria-label={title}>
      <div className="sticky top-0 z-[1] flex justify-between bg-(--z-side) px-[16px] pt-[10px] pb-[6px] font-(family-name:--mono) text-xs uppercase tracking-[0.14em] text-(--muted)">
        <span>{title}</span>
        {count != null && <span>{count}</span>}
      </div>
      <ul className="m-0 list-none px-[8px] pb-[8px]">{children}</ul>
    </section>
  );
}

export function SideItem({
  badge,
  badgeKey,
  title,
  count = null,
  sub,
  dot = false,
  active = false,
  onClick,
}: {
  badge: string;
  badgeKey?: string;
  title: string;
  count?: number | null;
  sub?: string | null;
  dot?: boolean;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        aria-current={active ? "page" : undefined}
        className={cn(
          "flex w-full cursor-pointer items-center gap-[10px] rounded-[12px] px-[10px] py-[8px] text-left text-sm text-(--ink)",
          "hover:bg-(--paper) focus-visible:outline-2 focus-visible:outline-(--brand)",
          active ? "bg-(--paper) outline-1 outline-(--line)" : null,
        )}
      >
        <span
          {...(badgeKey === undefined ? null : avatarToneProps(badgeKey, "soft"))}
          className="grid size-[28px] shrink-0 place-items-center rounded-[9px] bg-(--brand-soft) text-xs font-semibold text-(--brand)"
        >
          {badge}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate">
            {title}
            {count != null && <span className="font-quant text-(--muted)"> · {count}</span>}
          </span>
          {sub && (
            <small className="block truncate text-xs leading-[1.25] text-(--muted)">{sub}</small>
          )}
        </span>
        {dot && (
          <span aria-hidden className="ml-auto size-[6px] shrink-0 rounded-full bg-(--brand)" />
        )}
      </button>
    </li>
  );
}
